// /w/:wid/reports/:id — one report. Battle (attack / defence): headline seal, attacker and defender with their unit
// tables, loot, wall and loyalty damage, spy intel; trade: the exchange and the delivery; support, instant spy, revealed
// incoming and anything new get their own shapes. Prev/next walk the inbox tab it was opened from; the action bar has
// trash, SHARE (a public 7-day link, Oct 15 2026 — src/kit/ReportShare.tsx; its sheet has Post to Discord, so the report has
// no separate Discord button — Ben, Oct 15 2026), SIMULATE (Valor Tools) and ATTACK AGAIN (Rally Commands prefilled) or OPEN MARKET.
// The report's own sections (ReportContent) are also what a shared link shows (PublicReport.tsx): the same screen, seen
// from the sharer's side, with no share buttons and without what needs the player's account (ReportLook).
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ReportBody as Body, ReportListItem, ReportResources, ReportUnits } from "@/api/types";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { Countdown, StickyBar, UnitMedal } from "@/kit/city";
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp, IconCrown, IconKeep, IconPerson, IconTrash } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Bar, Button, Card, CardHead, Disc, Empty, Medallion, Row, RowText, Skeleton, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { useReportMutations, useReports } from "@/state/socialQueries";
import { useNow } from "@/state/clock";
import { useRememberedOpen } from "@/state/collapse";
import { BUILDING_NAMES, RESOURCE_KEYS, RESOURCE_NAMES, TROOP_KEYS, type BuildingKey, type ResourceKey } from "@/game/constants";
import { fmtCoords, fmtNum, parseCoords, plural } from "@/game/format";
import { fmtClock, parseGameTime } from "@/game/time";
import { wrapDistance } from "@/map/geo";
import {
  SEAL_WORD, buildingRows, cleanSubject, hasAny, haulInfo, inboxItems, isAttackerSide, isBarbarian, inTab, inboxTabOf, inboxTabParam, num, parties, rate, recipientWon, reportTime, reportType, resourceWords, sealOf, simulatorHash, simulatorUrl,
  sumResources, sumUnits, targetOf, unitRows, type Party,
} from "@/game/reports";
import { Subject } from "./ReportsInbox";
import { intelReady, reportCard } from "@/game/share";
import { ReportShareButton, useReportLink } from "@/kit/ReportShare";
import { openTools, useVtOn } from "@/state/vt";
import "../social.css";

/** How a report is being seen. links: profile / map / store buttons go somewhere (the viewer is signed in on that world);
 *  owner: the viewer is the player the report belongs to ("you", the Combat Medics tip). A shared link turns them off. */
export interface ReportLook { links: boolean; owner: boolean }
const LookCtx = createContext<ReportLook>({ links: true, owner: true });
export const ReportLookProvider = LookCtx.Provider;
const useLook = () => useContext(LookCtx);

export function ReportView() {
  return (
    <WorldPage>
      <Report />
    </WorldPage>
  );
}

const TITLES: Record<string, string> = { re_a: "Battle report", re_d: "Battle report", re_s: "Support report", re_t: "Trade report", re_is: "Spy report", re_i: "Incoming", re_gi: "Guild invite", re_gu: "Guild notice" };

export function Report() {
  const { id = "" } = useParams();
  const { wid, vid, v, base } = useWorldCity();
  const [params] = useSearchParams();

  const nav = useNavigate();
  const session = useSession();
  const q = useReports(wid);
  const { markRead, remove } = useReportMutations(wid);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const raw = q.data?.report_info?.newsfeed?.[id];
  const b = raw?.b;
  // 1 of 10 steps through the tab the report was opened from (?t=, old ?f= too), else the report's own tab
  const tab = inboxTabParam(params.get("t"), params.get("f")) ?? inboxTabOf(b);
  const list = useMemo(() => inboxItems(q.data).filter((it) => inTab(it.item.b, tab)), [q.data, tab]);
  const index = list.findIndex((it) => it.id === id);
  const entry = index >= 0 ? list[index] : null;

  // reading marks the report read (the list may have been opened from a bookmark or another device)
  const unread = !!raw && num(raw.r) === 0;
  useEffect(() => {
    if (unread) markRead.mutate(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, unread]);

  const go = (i: number) => { const t = list[i]; if (t) nav(`${base}/reports/${t.id}?t=${tab}`, { replace: true }); };
  const back = () => nav(`${base}/reports?t=${tab}`);
  const doDelete = async () => {
    setConfirmDelete(false);
    try {
      await remove.mutateAsync([id]);
      usePrompts.getState().toast("Report deleted.", "success");
      if (list.length > 1) go(index + 1 < list.length ? index + 1 : index - 1);
      else back();
    } catch (e) {
      usePrompts.getState().error(e);
    }
  };
  // the public link (Oct 15 2026): made when Share or Discord is first used, then the same link for 7 days
  const link = useReportLink(wid, id);
  // Valor Tools switched on for the player (Oct 15 2026): Simulate opens the Sim inside the Valor Tools app, signed in
  const vtOn = useVtOn(wid);

  const type = reportType(b);
  const title = TITLES[type] ?? "Report";

  return (
    <div className="stack rp-view">
      <div className="tbar rp-tbar">
        <Disc size="sm" className="split-back" label="Back to reports" onClick={back}><IconChevronLeft /></Disc>
        <div className="tbar-mid">
          <h1><span className="t">{title}</span></h1>
          {entry && <div className="tbar-sub rp-pos">{index + 1} of {list.length}</div>}
        </div>
        <div className="rp-steps">
          <Disc size="sm" label="Newer report" disabled={index <= 0} onClick={() => go(index - 1)}><IconChevronUp /></Disc>
          <Disc size="sm" label="Older report" disabled={index < 0 || index >= list.length - 1} onClick={() => go(index + 1)}><IconChevronDown /></Disc>
        </div>
      </div>

      {q.isLoading && !q.data && <><Skeleton h={210} /><Skeleton h={300} /></>}
      {q.data && !raw && <Card><Empty>This report is gone — it was deleted, or it fell off the game's latest hundred.</Empty></Card>}

      {b && raw && (
        <>
          <ReportContent raw={raw} uid={session.uid} base={base} vid={vid} size={v?.world_width} />

          <StickyBar className="rp-actions">
            <div className="acts">
              <Disc size="sm" label="Delete this report" className="rp-trash" onClick={() => setConfirmDelete(true)}><IconTrash /></Disc>
              {/* Share (Oct 15 2026): a public link anyone can open for 7 days. Guild invites and notices stay private. */}
              {type !== "re_gi" && type !== "re_gu" && <ReportShareButton link={link} title={cleanSubject(raw.s)} card={() => reportCard(b, cleanSubject(raw.s), reportTime(raw))} wid={wid} intelRid={intelReady(b) ? id : null} />}
              {(type === "re_a" || type === "re_d" || type === "re_is") && <Button kind="steel" size="md" onClick={() => (vtOn ? openTools(wid, `/app/?tab=sim&w=${wid}#${simulatorHash(b)}`, true) : window.open(simulatorUrl(b), "_blank", "noopener"))}>Simulate</Button>}
              {type === "re_t" && <Button kind="steel" size="md" onClick={() => nav(vid ? `${base}/city/${vid}/market` : base)}>Open market</Button>}
              {(type === "re_a" || type === "re_d" || type === "re_is") && <AttackAgain b={b} base={base} vid={vid} />}
              {type === "re_s" && <Button kind="crimson" size="md" onClick={() => nav(vid ? `${base}/city/${vid}/rally/troops` : base)}>Rally point</Button>}
              {type === "re_i" && <Button kind="crimson" size="md" onClick={() => nav(vid ? `${base}/city/${vid}/rally/movement` : base)}>Movement</Button>}
            </div>
          </StickyBar>
        </>
      )}

      <Confirm open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this report?" text="It can't be brought back." action="Delete" onConfirm={() => void doDelete()} busy={remove.isPending} />
    </div>
  );
}

/** The report's own sections, by type — the report screen and a shared link (PublicReport.tsx) both show these. */
export function ReportContent({ raw, uid, base, vid, size }: { raw: Pick<ReportListItem, "b" | "s" | "ts">; uid: number | null; base: string; vid: number | null; size?: number }) {
  const b = raw.b;
  const type = reportType(b);
  const ts = reportTime(raw);
  const subject = cleanSubject(raw.s);
  if (type === "re_a" || type === "re_d") return <Battle b={b} ts={ts} uid={uid} base={base} vid={vid} size={size} />;
  if (type === "re_t") return <Trade b={b} ts={ts} subject={subject} uid={uid} base={base} size={size} />;
  if (type === "re_s") return <Support b={b} ts={ts} subject={subject} uid={uid} base={base} />;
  if (type === "re_is") return <Spy b={b} ts={ts} subject={subject} uid={uid} base={base} />;
  if (type === "re_i") return <Incoming b={b} ts={ts} subject={subject} uid={uid} base={base} />;
  return <Generic b={b} ts={ts} subject={subject} />;
}

export const REPORT_TITLES = TITLES;

export function AttackAgain({ b, base, vid }: { b: Body; base: string; vid: number | null }) {
  const nav = useNavigate();
  const t = targetOf(b);
  const own = reportType(b) === "re_d";
  if (!t || !vid) return null;
  return (
    <Button kind="crimson" size="md" onClick={() => nav(`${base}/city/${vid}/rally/commands?x=${t[0]}&y=${t[1]}`)}>
      {own ? "Strike back" : "Attack again"}
    </Button>
  );
}

// ------------------------------------------------------------------ pieces

const when = (ms: number) => (Number.isFinite(ms) ? `${String(new Date(ms).getUTCMonth() + 1).padStart(2, "0")}/${String(new Date(ms).getUTCDate()).padStart(2, "0")} · ${fmtClock(ms, true)}` : "—");
const coordsOf = (p: Party) => (p.coords ? fmtCoords(p.coords) : "");

function Headline({ seal, eyebrow, tone, title, stats }: { seal: ReturnType<typeof sealOf>; eyebrow: string; tone?: "green" | "crim" | "gold"; title: ReactNode; stats: { label: string; value: ReactNode; tone?: "crim" | "green" }[] }) {
  return (
    <Card className="rp-head">
      <div className="rp-headrow">
        <Medallion src={art.social.seal(seal)} alt="" size={104} glow={seal === "victory" || seal === "losses" ? true : seal === "defeat" ? "crim" : undefined} className="rp-bigseal" />
        <div className="rp-headtxt">
          <div className={cx("rp-eyebrow", tone)}>{eyebrow}</div>
          <h2>{title}</h2>
        </div>
      </div>
      <div className="statband rp-stats">
        {stats.map((s, i) => (
          <div key={i}><div className="lbl">{s.label}</div><div className={cx("val", s.tone)}>{s.value}</div></div>
        ))}
      </div>
    </Card>
  );
}

function PartyRow({ p, you: mine, base, aside, note }: { p: Party; you: boolean; base: string; aside?: ReactNode; note?: ReactNode }) {
  const nav = useNavigate();
  const look = useLook();
  const you = mine && look.owner;
  const barb = isBarbarian(p);
  const xy = look.links ? parseCoords(p.coords) : null;
  return (
    <div className="rp-party">
      <div className="rp-partyrow">
        {you ? <Medallion src={art.avatar} alt="" size={64} glow /> : barb ? <Medallion size={64} className="rp-barb"><IconCrown /></Medallion> : <Medallion initial={p.login || "?"} size={64} />}
        <div className="rp-partytxt">
          <b>{barb ? "Barbarian" : p.login || "Unknown"}{you && <small> you</small>}</b>
          <span>{p.city ? <>{p.city} <i>{coordsOf(p)}</i></> : coordsOf(p)}</span>
        </div>
        <div className="rp-partyacts">
          {look.links && !barb && p.user_id > 0 && <Disc size="sm" label={`${p.login}'s profile`} onClick={() => nav(`${base}/player/${p.user_id}`)}><IconPerson /></Disc>}
          {xy && <Disc size="sm" label="Show on the map" onClick={() => nav(`${base}/map?x=${xy[0]}&y=${xy[1]}`)}><IconKeep /></Disc>}
        </div>
      </div>
      {aside}
      {note && <p className="rp-note">{note}</p>}
    </div>
  );
}

function UnitTable({ sent, lost, saved, cols = ["sent", "lost", "saved", "left"], tiers, labels }: { sent?: ReportUnits; lost?: ReportUnits; saved?: ReportUnits; cols?: ("sent" | "lost" | "saved" | "left")[]; tiers?: Record<string, number>; labels?: Partial<Record<"sent" | "lost" | "saved" | "left", string>> }) {
  const rows = unitRows(sent, lost, saved);
  if (!rows.length) return null;
  const head: Record<string, string> = { sent: "Sent", lost: "Lost", saved: "Saved", left: "Left", ...labels };
  const dash = <span className="dim">—</span>;
  return (
    <table className="tbl rp-units">
      <thead>
        <tr><th>Unit</th>{cols.map((c) => <th key={c} className={cx(c === "lost" && "crimtxt", c === "saved" && "greentxt")}>{head[c]}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className="name"><UnitMedal troop={r.key} tier={tiers?.[r.key] ?? 0} size={44} /> <span className="uname">{r.name}</span></td>
            {cols.map((c) => <td key={c} className={cx(c === "lost" && r.lost > 0 && "crimtxt", c === "saved" && r.saved > 0 && "greentxt")}>{r[c] > 0 ? fmtNum(r[c]) : dash}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ResourceTiles({ res, sign }: { res: ReportResources | undefined; sign?: "+" | "-" }) {
  const icon: Record<ResourceKey, string> = { r_w: art.hud.wood, r_c: art.hud.clay, r_i: art.hud.iron };
  return (
    <div className="c-grid3 rp-res">
      {RESOURCE_KEYS.map((k) => (
        <div key={k} className="c-tile rp-restile">
          <img src={icon[k]} alt={RESOURCE_NAMES[k]} />
          <b>{sign ?? ""}{fmtNum(num(res?.[k]))}</b>
        </div>
      ))}
    </div>
  );
}

/** Resources in the city · the fifteen buildings · troops outside: what scouts (or an instant spy) saw. */
export function SpyIntel({ b, kind, scouts }: { b: Body; kind: "spy" | "battle"; scouts?: number }) {
  // closed until opened, then remembered; the spy report and the battle report each keep their own state
  const [open, toggleOpen] = useRememberedOpen(`reports.intel.${kind}`);
  const hasRes = !!b.spied_resources;
  const hasB = !!b.spied_buildings && Object.keys(b.spied_buildings).length > 0;
  const hasOut = b.spied_units_out !== undefined;
  if (!hasRes && !hasB && !hasOut) return null;
  const rows = buildingRows(b.spied_buildings);
  const built = rows.filter((r) => r.level > 0).length;
  const wall = num(b.spied_buildings?.b_wa);
  const summary = [hasRes && "resources", hasB && `${built} buildings`, hasOut && "troops away"].filter(Boolean).join(" · ");
  return (
    <Card className="rp-intel">
      <CardHead title="Spy intel" aside={open ? (scouts ? `seen by ${plural(scouts, "Scout")}` : "") : summary} onToggle={toggleOpen} open={open} />
      {open && (
        <div className="stack" style={{ gap: 14 }}>
          {hasRes && (
            <section>
              <div className="c-section">Resources in the city<span className="aside">{fmtNum(sumResources(b.spied_resources))} in all</span></div>
              <ResourceTiles res={b.spied_resources} />
            </section>
          )}
          {hasB && (
            <section>
              <div className="c-section">Buildings<span className="aside">{built} of 15 built · {wall > 0 ? `wall ${wall}` : <span className="crimtxt">no wall</span>}</span></div>
              <div className="rp-bgrid">
                {rows.map((r) => (
                  <div key={r.key} className={cx("rp-b", r.level === 0 && "none", r.key === "b_wa" && r.level === 0 && "nowall")}>
                    <span>{r.name}</span><b>{r.level > 0 ? r.level : "—"}</b>
                  </div>
                ))}
              </div>
            </section>
          )}
          {hasOut && (
            <section>
              <div className="c-section">Troops outside the city<span className="aside">{hasAny(b.spied_units_out) ? plural(sumUnits(b.spied_units_out), "unit") : "none"}</span></div>
              <div className="rp-out">
                <div className="rp-outmedals">
                  {TROOP_KEYS.filter((k) => k !== "t_n").map((k) => <UnitMedal key={k} troop={k} tier={0} size={40} dim={num(b.spied_units_out?.[k]) === 0} count={num(b.spied_units_out?.[k]) || undefined} />)}
                </div>
                {!hasAny(b.spied_units_out) && <em>Every troop is home</em>}
              </div>
            </section>
          )}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ battle

function Battle({ b, ts, uid, base, vid, size }: { b: Body; ts: number; uid: number | null; base: string; vid: number | null; size?: number }) {
  const nav = useNavigate();
  const look = useLook();
  const won = recipientWon(b);
  const attackerSide = isAttackerSide(b);
  const { from: attacker, to: defender } = parties(b);
  const seal = sealOf(b);
  const youAttack = attacker.user_id === uid && uid !== null;
  const youDefend = defender.user_id === uid && uid !== null;
  const luck = num(b.luck);
  const winner = num(b.is_attacker_win) === 1 ? attacker : defender;
  const haul = haulInfo(b);
  const aSent = sumUnits(b.attacking_units), aLost = sumUnits(b.dead_attacking_units);
  const dKnown = b.defending_units !== undefined;
  const dSent = sumUnits(b.defending_units), dLost = sumUnits(b.dead_defending_units);
  const conquered = b.end_loyalty !== undefined && b.end_loyalty !== null && num(b.end_loyalty) <= 0;
  const scouts = num(b.attacking_units?.t_sc);
  const dist = size && attacker.coords && defender.coords ? wrapDistance(parseCoords(attacker.coords)!, parseCoords(defender.coords)!, size) : null;
  void vid;
  return (
    <>
      <Headline
        seal={seal}
        eyebrow={won === null ? "Battle" : attackerSide ? (won ? (conquered ? "Conquered" : "Victory") : "Defeat") : won ? "Defence held" : conquered ? "City lost" : "Defence broken"}
        tone={won === null ? "gold" : won ? "green" : "crim"}
        title={<><span>{attacker.city || attacker.login} {conquered ? "conquers" : "attacks"}</span><br /><span className="gold">{defender.city || defender.login}</span> <small>{coordsOf(defender)}</small></>}
        stats={[
          { label: "Time of attack", value: <span className="sb-wrap">{when(ts)}</span> },
          { label: "Luck", value: <span className={luck < 0 ? "crimtxt" : luck > 0 ? "greentxt" : undefined}>{luck > 0 ? "+" : luck < 0 ? "−" : ""}{Math.abs(luck)}%</span> },
          { label: "Morale", value: `${num(b.morale) || 100}%` },
          { label: "Winner", value: <span className="gold">{isBarbarian(winner) ? "Barbarian" : winner.login}</span> },
        ]}
      />

      <Card>
        <CardHead title="Attacker" aside={`${fmtNum(aSent)} sent · ${fmtNum(aLost)} lost`} />
        <PartyRow p={attacker} you={youAttack} base={base} />
        <UnitTable sent={b.attacking_units} lost={b.dead_attacking_units} saved={b.dead_attacking_units_saved} />
        {look.links && look.owner && attackerSide && aLost > 0 && !hasAny(b.dead_attacking_units_saved) && (
          <button type="button" className="rp-tip" onClick={() => nav(vid ? `${base}/city/${vid}/store` : base)}>Save more troops next time with <b>Combat Medics</b> <IconChevronRight /></button>
        )}
        {hasAny(b.dead_attacking_units_saved) && <p className="rp-note greentxt">Combat Medics brought {fmtNum(sumUnits(b.dead_attacking_units_saved))} back.</p>}
      </Card>

      <Card>
        <CardHead title="Defender" aside={dKnown ? (dSent ? `${fmtNum(dSent)} home · ${fmtNum(dLost)} lost` : "No troops") : "Unseen"} />
        <PartyRow
          p={defender}
          you={youDefend}
          base={base}
          note={!dKnown ? "Your scouts didn't get through — the garrison stayed unseen." : dSent === 0 ? (isBarbarian(defender) ? "The city was undefended — nothing stood against your troops." : "Nobody was home to defend.") : undefined}
        />
        {dKnown && dSent > 0 && <UnitTable sent={b.defending_units} lost={b.dead_defending_units} saved={b.dead_defending_units_saved} labels={{ sent: "Home" }} />}
        {hasAny(b.dead_defending_units_saved) && <p className="rp-note greentxt">Combat Medics saved {fmtNum(sumUnits(b.dead_defending_units_saved))} defenders.</p>}
      </Card>

      {haul && (
        <Card>
          <CardHead title="Loot" aside={`${fmtNum(haul.total)} hauled${haul.full ? " · full" : ""}`} />
          <ResourceTiles res={b.haul} />
          <div className="rp-cap">
            <span className="lbl">Capacity</span>
            <Bar value={haul.capacity ? haul.total / haul.capacity : 0} color={haul.full ? "green" : "gold"} />
            <span className="val">{fmtNum(haul.total)} / {fmtNum(haul.capacity)}{haul.boost > 0 && <span className="greentxt"> · +{fmtNum(haul.boost)} boosted</span>}</span>
          </div>
        </Card>
      )}

      {(b.original_wall_level !== undefined || b.cat_target) && (
        <Card>
          <CardHead title="Siege" aside={dist !== null ? `${dist.toFixed(1)} fields` : undefined} />
          <div className="c-kvs">
            {b.original_wall_level !== undefined && (
              <Row>
                <RowText line={<>Wall</>} meta="rams" />
                <div className="right"><div className="val">{num(b.original_wall_level)} <span className="muted">→</span> <b className={num(b.resulting_wall_level) < num(b.original_wall_level) ? "crimtxt" : undefined}>{num(b.resulting_wall_level ?? b.original_wall_level)}</b></div></div>
              </Row>
            )}
            {b.cat_target && (
              <Row>
                <RowText line={<>{BUILDING_NAMES[b.cat_target as BuildingKey] ?? String(b.cat_target)}</>} meta="ballistae" />
                <div className="right"><div className="val">{num(b.original_cat_target_level)} <span className="muted">→</span> <b className={num(b.resulting_cat_target_level) < num(b.original_cat_target_level) ? "crimtxt" : undefined}>{num(b.resulting_cat_target_level)}</b></div></div>
              </Row>
            )}
          </div>
        </Card>
      )}

      {b.start_loyalty !== undefined && b.start_loyalty !== null && (
        <Card>
          <CardHead title="Loyalty" aside={conquered ? "the city changed hands" : "scholars"} />
          <div className="rp-loy">
            <b>{num(b.start_loyalty)}</b><span className="muted">→</span><b className={conquered ? "crimtxt" : "gold"}>{num(b.end_loyalty)}</b>
            {num(b.scholar_effect_additional) !== 0 && <small className="muted">{num(b.scholar_effect_additional) > 0 ? "+" : ""}{num(b.scholar_effect_additional)} from effects</small>}
          </div>
        </Card>
      )}

      <SpyIntel b={b} kind="battle" scouts={scouts || undefined} />
    </>
  );
}

// ------------------------------------------------------------------ trade

function Trade({ b, ts, subject, uid, base, size }: { b: Body; ts: number; subject: string; uid: number | null; base: string; size?: number }) {
  const { from, to } = parties(b);
  const offered = !!b.offered_char;
  const inn = sumResources(b.in_resources), out = sumResources(b.out_resources);
  const a = parseCoords(from.coords), c = parseCoords(to.coords);
  const dist = size && a && c ? wrapDistance(a, c, size) : null;
  const kind = b.report_subtype === "re_ts" ? (inn && !out ? "Resources received" : "Resources sent") : b.report_subtype === "re_ta" ? "Offer accepted" : "Trade complete";
  const mine = (p: Party) => uid !== null && p.user_id === uid;
  // what each side sent: the report owner's "out" went from the owner's side; "in" came from the other side
  const ownerIsFrom = mine(from);
  const fromSent = ownerIsFrom ? b.out_resources : b.in_resources;
  const toSent = ownerIsFrom ? b.in_resources : b.out_resources;
  return (
    <>
      <Headline
        seal="trade"
        eyebrow={kind}
        tone="gold"
        title={<Subject text={subject} />}
        stats={[
          { label: "Rate", value: inn && out ? rate(out, inn) : "—" },
          { label: "Distance", value: dist !== null ? `${dist.toFixed(1)} fields` : "—" },
          { label: b.report_subtype === "re_ta" ? "Accepted" : "Completed", value: <span className="sb-wrap">{when(ts)}</span> },
        ]}
      />
      <Card>
        <CardHead title="The exchange" />
        <div className="rp-exchange">
          <TradeSide label={offered ? "Offered by" : "From"} p={from} you={mine(from)} res={fromSent} base={base} />
          <div className="rp-arrows" aria-hidden="true"><i /><span /><i className="back" /></div>
          <TradeSide label={offered ? "Accepted by" : "To"} p={to} you={mine(to)} res={toSent} base={base} />
        </div>
      </Card>
      <Card>
        <CardHead title="Delivery" />
        <div className="rows">
          {inn > 0 && RESOURCE_KEYS.filter((k) => num(b.in_resources?.[k]) > 0).map((k) => (
            <Row key={k} className="rp-deliver">
              <img src={k === "r_w" ? art.hud.wood : k === "r_c" ? art.hud.clay : art.hud.iron} alt="" />
              <RowText line={<b className="greentxt" style={{ color: "var(--green-ink)" }}>+{fmtNum(num(b.in_resources?.[k]))} {RESOURCE_NAMES[k].toLowerCase()}</b>} />
              <div className="right"><div className="val muted">into <b>{(ownerIsFrom ? from : to).city ?? "your city"}</b></div></div>
            </Row>
          ))}
          {out > 0 && RESOURCE_KEYS.filter((k) => num(b.out_resources?.[k]) > 0).map((k) => (
            <Row key={k} className="rp-deliver">
              <img src={k === "r_w" ? art.hud.wood : k === "r_c" ? art.hud.clay : art.hud.iron} alt="" />
              <RowText line={<b>−{fmtNum(num(b.out_resources?.[k]))} {RESOURCE_NAMES[k].toLowerCase()}</b>} />
              <div className="right"><div className="val muted">to <b>{(ownerIsFrom ? to : from).city ?? "the other city"}</b></div></div>
            </Row>
          ))}
          {!inn && !out && <Empty>No resources are listed on this report.</Empty>}
        </div>
        <p className="c-note">{b.report_subtype === "re_ts" ? "Merchants return home on their own." : "The merchants have returned home."}</p>
      </Card>
    </>
  );
}

function TradeSide({ label, p, you: mine, res, base }: { label: string; p: Party; you: boolean; res: ReportResources | undefined; base: string }) {
  const nav = useNavigate();
  const look = useLook();
  const you = mine && look.owner;
  const words = resourceWords(res);
  return (
    <div className="rp-side">
      <div className="lbl">{label}</div>
      <button type="button" className="rp-sidewho" disabled={!look.links} onClick={() => (look.links && p.user_id > 0 ? nav(`${base}/player/${p.user_id}`) : undefined)}>
        {you ? <Medallion src={art.avatar} alt="" size={64} glow /> : <Medallion initial={p.login || "?"} size={64} />}
        <b>{p.login || "Unknown"}</b>
        <span>{p.city} {coordsOf(p)}</span>
      </button>
      <div className="rp-sidesent">
        <div className="lbl">Sent</div>
        {words ? RESOURCE_KEYS.filter((k) => num(res?.[k]) > 0).map((k) => (
          <div key={k} className="rp-sideres"><img src={k === "r_w" ? art.hud.wood : k === "r_c" ? art.hud.clay : art.hud.iron} alt="" /><b>{fmtNum(num(res?.[k]))}</b><small>{RESOURCE_NAMES[k].toLowerCase()}</small></div>
        )) : <div className="rp-sideres"><small className="muted">nothing</small></div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ support

function Support({ b, ts, subject, uid, base }: { b: Body; ts: number; subject: string; uid: number | null; base: string }) {
  const { from, to } = parties(b);
  const attacked = !!b.attacking_char || hasAny(b.dead_units);
  const n = sumUnits(b.units), dead = sumUnits(b.dead_units);
  const mine = (p: Party) => uid !== null && p.user_id === uid;
  return (
    <>
      <Headline
        seal="support"
        eyebrow={attacked ? "Support attacked" : "Support arrived"}
        tone={attacked ? "crim" : "gold"}
        title={<Subject text={subject} />}
        stats={[
          { label: attacked ? "Attacked" : "Arrived", value: <span className="sb-wrap">{when(ts)}</span> },
          { label: "Units", value: fmtNum(n) },
          ...(attacked ? [{ label: "Lost", value: fmtNum(dead), tone: "crim" as const }] : [{ label: "Standing in", value: <span className="gold">{to.city ?? "—"}</span> }]),
        ]}
      />
      {attacked && b.attacking_char && (
        <Card>
          <CardHead title="Attacker" />
          <PartyRow p={{ login: b.attacking_char.login, user_id: num(b.attacking_char.user_id), city: b.attacking_village?.city_name, coords: b.attacking_village?.coords }} you={false} base={base} />
        </Card>
      )}
      <Card>
        <CardHead title="Supporter" aside={from.city} />
        <PartyRow p={from} you={mine(from)} base={base} />
        <UnitTable sent={b.units} lost={b.dead_units} cols={attacked ? ["sent", "lost", "left"] : ["sent"]} />
      </Card>
      <Card>
        <CardHead title="Supported city" />
        <PartyRow p={to} you={mine(to)} base={base} note={attacked ? undefined : "These troops now stand in this city's defence. Recall them from the Rally Point."} />
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ instant spy

function Spy({ b, ts, subject, uid, base }: { b: Body; ts: number; subject: string; uid: number | null; base: string }) {
  const { to } = parties(b);
  const home = sumUnits(b.defending_units);
  const wall = num(b.spied_buildings?.b_wa);
  return (
    <>
      <Headline
        seal="spy"
        eyebrow="Instant spy"
        tone="gold"
        title={<Subject text={subject} />}
        stats={[
          { label: "Spied", value: <span className="sb-wrap">{when(ts)}</span> },
          { label: "Troops home", value: fmtNum(home) },
          { label: "Wall", value: wall > 0 ? String(wall) : <span className="crimtxt">none</span> },
        ]}
      />
      <Card>
        <CardHead title="The city" aside={to.coords ? fmtCoords(to.coords) : undefined} />
        <PartyRow p={to} you={uid !== null && to.user_id === uid} base={base} />
        {home > 0 ? <UnitTable sent={b.defending_units} cols={["sent"]} labels={{ sent: "Home" }} /> : <Empty>No troops at home.</Empty>}
      </Card>
      <SpyIntel b={b} kind="spy" />
    </>
  );
}

// ------------------------------------------------------------------ revealed incoming

function Incoming({ b, ts, subject, uid, base }: { b: Body; ts: number; subject: string; uid: number | null; base: string }) {
  const now = useNow();
  const { from, to } = parties(b);
  const land = parseGameTime(b.end_time);
  const n = sumUnits(b.incoming_units);
  return (
    <>
      <Headline
        seal="incoming"
        eyebrow={Number.isFinite(land) && land > now ? "Incoming — revealed" : "Incoming — landed"}
        tone="crim"
        title={<Subject text={subject} />}
        stats={[
          { label: "Revealed", value: <span className="sb-wrap">{when(ts)}</span> },
          { label: "Lands", value: Number.isFinite(land) ? (land > now ? <Countdown endMs={land} /> : fmtClock(land)) : "—", tone: Number.isFinite(land) && land > now ? "crim" : undefined },
          { label: "Units", value: fmtNum(n), tone: "crim" },
        ]}
      />
      <Card>
        <CardHead title="Attacker" />
        <PartyRow p={from} you={false} base={base} />
        <UnitTable sent={b.incoming_units} cols={["sent"]} labels={{ sent: "Marching" }} />
      </Card>
      <Card>
        <CardHead title="Target" />
        <PartyRow p={to} you={uid !== null && to.user_id === uid} base={base} note="Pull troops home or call for support from the Rally Point." />
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ anything else

function Generic({ b, ts, subject }: { b: Body; ts: number; subject: string }) {
  const entries = Object.entries(b).filter(([k, v]) => k !== "report_type" && v !== null && v !== undefined && typeof v !== "object");
  return (
    <>
      <Headline seal={sealOf(b)} eyebrow={SEAL_WORD[sealOf(b)]} tone="gold" title={subject} stats={[{ label: "When", value: <span className="sb-wrap">{when(ts)}</span> }]} />
      <Card>
        <CardHead title="Details" />
        <div className="c-kvs">
          {entries.map(([k, v]) => <Row key={k}><RowText line={k.replace(/_/g, " ")} /><div className="right"><div className="val">{String(v)}</div></div></Row>)}
          {!entries.length && <Empty>Nothing more to show.</Empty>}
        </div>
      </Card>
    </>
  );
}
